import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Flex,
  Icon,
  toast,
  type IIconProps,
} from '@kognitos/lattice';

import type { V1PhaseSubType } from '@/generated/hooks/grimoire/models';
import {
  getExtractAutomationCodeConnectionsQueryKey,
  useAddAutomationConnections,
} from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { usePermissions } from '@/shared/permissions';
import {
  checkIsConflictError,
  errorHandler,
} from '@/shared/utils/error-handler';
import { isAxiosError } from 'axios';

import {
  getValidateAutomationImportQueryKey,
  useImportValidation,
} from '../../hooks/useImportValidation';
import { ChatIntegrationConnection } from '../QuillChat/components/ChatIntegrationConnection';

export interface IImportChecklistCardProps {
  automationId: string;
  /**
   * Gates the underlying validation query so it only runs where the checklist
   * is relevant (drafts). Defaults to true. The card renders nothing until a
   * draft is confirmed as imported, so this avoids firing the validate call in
   * the published editor.
   */
  enabled?: boolean;
  className?: string;
}

type TSectionStatus = 'done' | 'pending' | 'error';

const STATUS_ICON: Record<TSectionStatus, IIconProps['type']> = {
  done: 'CircleCheck',
  pending: 'CircleDashed',
  error: 'CircleX',
};

const STATUS_ICON_CLASS: Record<TSectionStatus, string> = {
  done: 'text-success',
  pending: 'text-muted-foreground',
  error: 'text-destructive',
};

/** Maps a trigger sub-type enum value to its translation key suffix. */
const TRIGGER_KIND_KEY: Partial<Record<V1PhaseSubType, string>> = {
  PHASE_SUB_TYPE_EMAIL: 'email',
  PHASE_SUB_TYPE_SCHEDULE: 'schedule',
  PHASE_SUB_TYPE_WEBHOOK: 'webhook',
  PHASE_SUB_TYPE_API: 'api',
};

/**
 * True when the add-connection call failed only because the connection is
 * already bound to the automation. Grimoire returns this as INVALID_ARGUMENT
 * (gRPC code 3 -> HTTP 400) with a "already exists" message, so the generic
 * `checkIsConflictError` (HTTP 409 only) doesn't catch it. We treat this as a
 * success: the desired end state (connection present) already holds.
 */
const isAlreadyExistsError = (error: unknown): boolean => {
  if (checkIsConflictError(error)) return true;
  if (isAxiosError(error)) {
    const message = error.response?.data?.message;
    return (
      typeof message === 'string' &&
      message.toLowerCase().includes('already exists')
    );
  }
  return false;
};

interface IChecklistSectionProps {
  status: TSectionStatus;
  title: string;
  children: React.ReactNode;
}

/** A status-iconed section header with its rows. */
function ChecklistSection({ status, title, children }: IChecklistSectionProps) {
  return (
    <Flex direction="column" gap={1} width="full" className="min-w-0">
      <Flex align="center" gap={2} className="min-w-0">
        <Icon
          type={STATUS_ICON[status]}
          size={20}
          className={cn('shrink-0', STATUS_ICON_CLASS[status])}
        />
        <span className="truncate text-base font-medium text-foreground">
          {title}
        </span>
      </Flex>
      <Flex direction="column" gap={1} width="full" className="pl-7 min-w-0">
        {children}
      </Flex>
    </Flex>
  );
}

interface IChecklistRowProps {
  label: string;
  /** Strikethrough + muted to indicate a completed item. */
  done?: boolean;
  action?: React.ReactNode;
}

/** A single text row with an optional right-aligned action. */
function ChecklistRow({ label, done, action }: IChecklistRowProps) {
  return (
    <Flex
      align="center"
      justify="between"
      gap={2}
      width="full"
      className="min-w-0"
    >
      <span
        className={cn(
          'truncate text-sm',
          done ? 'line-through text-muted-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      {action && <div className="shrink-0">{action}</div>}
    </Flex>
  );
}

/**
 * "Review what you imported" checklist shown at the top of the chat column when
 * a draft was created by importing or moving an automation. It surfaces what is
 * still missing (integrations to confirm, connections to wire up, triggers to
 * recreate) so the user can finish setup before running. Renders nothing when
 * the draft was not imported.
 */
export function ImportChecklistCard({
  automationId,
  enabled = true,
  className,
}: IImportChecklistCardProps) {
  const { t } = useTranslation('automation');

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { isImported, missingConnections, missingTriggerKinds } =
    useImportValidation({ organizationId, workspaceId, automationId, enabled });

  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManageAutomationConnections = hasPermission(
    ['AddAutomationConnections'],
    { organization: organizationId, workspace: workspaceId },
  );

  // Refresh both the import checklist (so the resolved row drops off) and the
  // top-row integrations panel after a connection is bound.
  const invalidateConnectionQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getValidateAutomationImportQueryKey(
        organizationId,
        workspaceId,
        automationId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getExtractAutomationCodeConnectionsQueryKey(
        organizationId ?? '',
        workspaceId ?? '',
        automationId,
      ),
    });
  };

  const { mutate: addAutomationConnections } = useAddAutomationConnections({
    mutation: {
      onSuccess: () => {
        invalidateConnectionQueries();
        toast.success(t('automation.importChecklist.connections.addSuccess'));
      },
      onError: error => {
        // Connection already bound: the desired state holds, so refresh
        // quietly instead of surfacing a scary error.
        if (isAlreadyExistsError(error)) {
          invalidateConnectionQueries();
          return;
        }
        errorHandler(error, () => {
          toast.error(t('automation.importChecklist.connections.addError'));
        });
      },
    },
  });

  const hasOutstanding =
    missingConnections.length > 0 || missingTriggerKinds.length > 0;
  const [isExpanded, setIsExpanded] = useState(true);

  // Hide entirely for non-imported drafts (and while the query is resolving,
  // `isImported` is false, so the card simply doesn't flash in).
  if (!isImported) {
    return null;
  }

  // Distinct integrations referenced by the missing connections.
  const integrationNames = Array.from(
    new Map(
      missingConnections
        .filter(c => c.book_name)
        .map(c => [
          c.book_name as string,
          c.source_connection_display_name || (c.book_name as string),
        ]),
    ).entries(),
  );

  const chevron: IIconProps['type'] = isExpanded ? 'ChevronUp' : 'ChevronRight';

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      data-testid="import-checklist-card"
      data-completion={hasOutstanding ? 'incomplete' : 'complete'}
      className={cn(
        'mb-2 w-full overflow-hidden rounded-xl border border-border bg-card',
        className,
      )}
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          type="button"
          className="flex h-auto w-full items-center justify-start gap-4 rounded-none px-5 py-5 text-left"
        >
          <Icon
            type="ArrowRight"
            size={20}
            className="shrink-0 text-foreground"
          />
          <Flex direction="column" className="min-w-0 flex-1">
            <span className="truncate text-base font-medium text-card-foreground">
              {t('automation.importChecklist.title')}
            </span>
            {isExpanded && (
              <span className="text-sm text-muted-foreground">
                {t('automation.importChecklist.subtitle')}
              </span>
            )}
          </Flex>
          <Icon
            type={chevron}
            size={16}
            className="shrink-0 text-muted-foreground"
          />
        </Button>
      </CollapsibleTrigger>

      {/* `forceMount` keeps the body mounted across collapse/expand — Radix
          Collapsible otherwise unmounts the content when closed, which is what
          wiped the `ChatIntegrationConnection` widgets' selected-connection
          state. Because the content stays mounted, we collapse it with a static
          `data-[state=closed]:hidden` (matching the original instant
          show/hide) rather than the height animation, whose keyframe reverts to
          full height once it ends on a still-mounted element. */}
      <CollapsibleContent
        forceMount
        className="overflow-hidden data-[state=closed]:hidden"
      >
        <Flex
          direction="column"
          gap={5}
          width="full"
          className="max-h-[50vh] overflow-y-auto px-5 pb-5"
        >
          <ChecklistSection
            status="done"
            title={t('automation.importChecklist.steps.title')}
          >
            <ChecklistRow
              label={t('automation.importChecklist.steps.valid')}
              done
            />
          </ChecklistSection>

          {integrationNames.length > 0 && (
            <ChecklistSection
              status="pending"
              title={t('automation.importChecklist.integrations.title')}
            >
              {integrationNames.map(([bookName, displayName]) => (
                <ChecklistRow
                  key={bookName}
                  label={t('automation.importChecklist.integrations.confirm', {
                    name: displayName,
                  })}
                />
              ))}
            </ChecklistSection>
          )}

          {missingConnections.length > 0 && (
            <ChecklistSection
              status="pending"
              title={t('automation.importChecklist.connections.title')}
            >
              {missingConnections.map((connection, index) =>
                // `alias` is part of the guard because it is the
                // `connection_alias` we persist — without it the binding would
                // be sent unaliased. Aliasless rows fall through to the
                // read-only ChecklistRow.
                connection.book_name &&
                connection.book_version &&
                connection.alias &&
                canManageAutomationConnections ? (
                  <ChatIntegrationConnection
                    key={`${connection.book_name}-${connection.alias}`}
                    integration_name={connection.book_name}
                    integration_version={connection.book_version}
                    requestId={`${automationId}:${connection.book_name}-${connection.alias}`}
                    // Differentiates multiple connections of the same
                    // integration (e.g. two Slack) in the checklist. Display
                    // only — binding still uses `connection.alias` below.
                    connectionLabel={
                      connection.source_connection_display_name ||
                      connection.alias ||
                      undefined
                    }
                    // Persist the chosen connection onto the imported draft.
                    // The widget only fires this once the connection is READY,
                    // so it is safe to bind immediately. `connection` is
                    // captured per row so each binding uses its own alias.
                    onSelectConnection={chatConnection => {
                      addAutomationConnections({
                        organization: organizationId ?? '',
                        workspace: workspaceId ?? '',
                        automation: automationId,
                        data: {
                          connections: [
                            {
                              // Cast is required: the `&&` guard in the JSX
                              // ternary narrows these here, but TS loses that
                              // narrowing inside this async callback boundary.
                              book: connection.book_name as string,
                              book_version: connection.book_version as string,
                              connection_id: chatConnection.connection_id,
                              connection_alias: connection.alias,
                            },
                          ],
                        },
                      });
                    }}
                  />
                ) : (
                  <ChecklistRow
                    key={`${connection.alias ?? index}`}
                    label={
                      connection.source_connection_display_name ||
                      connection.alias ||
                      ''
                    }
                  />
                ),
              )}
            </ChecklistSection>
          )}

          {missingTriggerKinds.length > 0 && (
            <ChecklistSection
              status="error"
              title={t('automation.importChecklist.triggers.title')}
            >
              {/* `missing_trigger_kinds` is a distinct set per the API, so the
                  enum value is a stable key. */}
              {Array.from(new Set(missingTriggerKinds)).map(kind => {
                const kindKey = TRIGGER_KIND_KEY[kind];
                const kindLabel = kindKey
                  ? t(`automation.importChecklist.triggers.kind.${kindKey}`)
                  : '';
                return (
                  <ChecklistRow
                    key={kind}
                    label={t('automation.importChecklist.triggers.recreate', {
                      kind: kindLabel,
                    })}
                  />
                );
              })}
            </ChecklistSection>
          )}
        </Flex>
      </CollapsibleContent>
    </Collapsible>
  );
}
